/**
 * Feature price list store — the SPA's single read of `GET /api/v1/credits/costs`.
 *
 * Every « ~N crédits » badge ({@link CreditCost}) resolves its number here, so no
 * cost is ever hard-coded in a component. Loaded once per session (shared
 * in-flight promise), re-fetched on demand via `reload()`.
 */
import { useEffect } from 'react';
import { create } from 'zustand';

import { getFeatureCosts } from '@/lib/api/credits';

interface CostsState {
  costs: Record<string, number> | null;
  fallback: number | null;
  loading: boolean;
  failed: boolean;
  load: (opts?: { force?: boolean }) => Promise<void>;
}

let inflight: Promise<void> | null = null;

// Unit-test seam: src/test/setup.ts pre-seeds an empty price list here so page
// tests never fire GET /credits/costs at their own fetch mocks. Never set in the app.
const SEEDED_COSTS =
  ((globalThis as Record<string, unknown>).__PSN_TEST_CREDIT_COSTS__ as Record<string, number> | undefined) ?? null;

export const useCreditCostsStore = create<CostsState>((set, get) => ({
  costs: SEEDED_COSTS,
  fallback: null,
  loading: false,
  failed: false,
  load: async (opts) => {
    if (get().costs && !opts?.force) return;
    if (inflight) return inflight;
    set({ loading: true });
    inflight = getFeatureCosts()
      .then((res) => set({ costs: res.costs ?? {}, fallback: res.default ?? null, failed: false }))
      .catch(() => set({ failed: true }))
      .finally(() => {
        set({ loading: false });
        inflight = null;
      });
    return inflight;
  },
}));

/** Price (credits) of `feature`, or null while loading / when the price list is unreachable. */
export function useFeatureCost(feature: string | undefined | null): number | null {
  const costs = useCreditCostsStore((s) => s.costs);
  const fallback = useCreditCostsStore((s) => s.fallback);
  const load = useCreditCostsStore((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  if (!feature || !costs) return null;
  const v = costs[feature];
  return typeof v === 'number' ? v : fallback;
}

/** Sum the prices of several features (a multi-step action); null until all are known. */
export function useFeatureCostSum(features: readonly string[]): number | null {
  const costs = useCreditCostsStore((s) => s.costs);
  const fallback = useCreditCostsStore((s) => s.fallback);
  const load = useCreditCostsStore((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  if (!costs) return null;
  let sum = 0;
  for (const f of features) {
    const v = costs[f] ?? fallback;
    if (typeof v !== 'number') return null;
    sum += v;
  }
  return sum;
}
